import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'

import { replaceLast } from 'app/common/lib/path'
import type { DietFoodType, DietFoodTypeId } from 'app/dataTypes/org/dietFoodTypes'

type Options = {
  items: Array<DietFoodType>
  loading: boolean
}

type Result = {
  dietFoodTypeId: DietFoodTypeId | undefined
  onSelectDietFoodType: (id: DietFoodTypeId | null) => void
}

const normalizeParam = (
  value: string | undefined,
  items: Array<DietFoodType>,
): DietFoodTypeId | undefined => {
  if (!value || value === 'base') return undefined
  return items.find(item => item.id === value)?.id
}

export function useDietFoodTypePath({ items, loading }: Options): Result {
  const history = useHistory()
  const location = useLocation()
  const { dietFoodTypeId: dietFoodTypeIdParam } = useParams<{ dietFoodTypeId?: string }>()
  const [dietFoodTypeId, setDietFoodTypeId] = useState<DietFoodTypeId | undefined>(undefined)

  const dietFoodTypesReady = !loading
  const hasDietFoodTypes = dietFoodTypesReady && items.length !== 0

  const updateDietFoodTypePath = useCallback((value: DietFoodTypeId | undefined) => {
    if (!dietFoodTypesReady || !hasDietFoodTypes) return
    const nextValue = value ?? 'base'
    const basePath = dietFoodTypeIdParam ? replaceLast(location.pathname) : location.pathname
    const nextPath = `${basePath}/${nextValue}`
    if (nextPath !== location.pathname) {
      history.replace(nextPath)
    }
  }, [dietFoodTypeIdParam, dietFoodTypesReady, hasDietFoodTypes, history, location.pathname])

  const onSelectDietFoodType = useCallback((id: DietFoodTypeId | null) => {
    const nextValue = id ?? undefined
    setDietFoodTypeId(nextValue)
    updateDietFoodTypePath(nextValue)
  }, [updateDietFoodTypePath])

  useEffect(() => {
    if (!dietFoodTypesReady || !hasDietFoodTypes) return
    const normalized = normalizeParam(dietFoodTypeIdParam, items)
    setDietFoodTypeId(prev => (prev === normalized ? prev : normalized))
  }, [dietFoodTypeIdParam, dietFoodTypesReady, hasDietFoodTypes, items])

  useEffect(() => {
    if (!dietFoodTypesReady) return
    if (!hasDietFoodTypes) {
      setDietFoodTypeId(undefined)
      if (dietFoodTypeIdParam) {
        const nextPath = replaceLast(location.pathname)
        if (nextPath !== location.pathname) {
          history.replace(nextPath)
        }
      }
      return
    }
    const normalized = normalizeParam(dietFoodTypeIdParam, items)
    const paramIsValid = dietFoodTypeIdParam === 'base' || (!!dietFoodTypeIdParam && !!normalized)
    if (!dietFoodTypeIdParam || !paramIsValid) {
      updateDietFoodTypePath(dietFoodTypeId)
    }
  }, [
    dietFoodTypeId,
    dietFoodTypeIdParam,
    dietFoodTypesReady,
    hasDietFoodTypes,
    history,
    items,
    location.pathname,
    updateDietFoodTypePath,
  ])

  return {
    dietFoodTypeId,
    onSelectDietFoodType,
  }
}
